import { LitElement, html, css } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import {
  fireAdobeAnalyticsEvent,
  handleFileDownloadEvent,
  analyticsCtaDataObject,
  handleAdobeNavEvent,
} from '../../../../helpers/adobe-analytics';
class CtaButton extends LitElement {
  static get properties() {
    return {
      hidden: { type: Boolean },
      disabled: { type: Boolean },
      ctaData: { type: Object },
      ctaAnalyticsData: { type: Object },
    };
  }

  constructor() {
    super();
    this.ctaAnalyticsData = {};
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      css`
        a {
          display: flex;
          flex-direction: column;
          justify-content: center;
          border: 1px solid #222;
          width: max-content;
          /* This is sometimes not rendering as 42px for some reason */
          /* height: calc(42px / var(--default-rem-px-count)); */
          height: 42px;
          cursor: pointer;
        }

        a > .cta-button {
          width: 100%;
          display: flex;
          justify-content: space-between;
          white-space: nowrap;
          height: fit-content;
          align-items: center;
        }

        a > .cta-button > p {
          font-size: 0.875em;
          font-weight: 500;
          letter-spacing: -0.24px;
          line-height: 1.25em;
          padding: 0 calc(16em / var(--default-rem-px-count));
          margin-bottom: calc(-2em / var(--default-rem-px-count));
          text-wrap: balance;
          height: fit-content;
        }

        img {
          padding-right: calc(16em / var(--default-rem-px-count));
          width: calc(16em / var(--default-rem-px-count));
          height: calc(16em / var(--default-rem-px-count));
        }

        img.smaller-svg {
          width: calc(12em / var(--default-rem-px-count)) !important;
          height: calc(12em / var(--default-rem-px-count)) !important;
        }

        .primary-colors,
        .primary-colors > span > p {
          background-color: #222222;
          color: #ffffff;
        }

        .secondary-colors {
          background-color: transparent;
          color: inherit;
        }

        a.disabled:not(.secondary-colors) > span > p {
          background-color: var(--grey-dark);
        }

        a.disabled {
          cursor: default;
        }

        a.primary-colors.disabled {
          background-color: var(--grey-dark);
          border-color: var(--grey-dark);
        }

        a.secondary-colors.disabled .cta-button p {
          color: var(--grey-dark);
        }
      `,
    ];
  }

  render() {
    return !!this.parsedCtaData
      ? html`
          <a
            href=${ifDefined(this.urlString)}
            target=${ifDefined(this.targetValue)}
            class="${classMap(this.linkClasses)}"
            style="${styleMap(this.buttonWidth)}"
            @click="${this._handleClick}"
          >
            <span class="cta-button">
              <p>
                ${this.textString}
              </p>
              <img class="${classMap(this.svgClasses)}" src=${this.svgUrl} />
            </span>
          </a>
        `
      : html``;
  }

  _handleClick(event) {
    if (this.urlString === '#contact-rep') {
      MicroModal.show('modal-contact-rep');
    }
    this.sendAnalyticsEvent(event);
  }

  get isPrimary() {
    return (
      this.getAttribute('cta-type') === 'primary' || this.ctaData.isPrimary
    );
  }

  get buttonWidth() {
    return this.ctaData.fullWidth
      ? { width: '100%' }
      : { width: 'max-content' };
  }

  get svgUrl() {
    if (this.isDownload) {
      return '/static/icons/SVG/download-10x10.svg';
    }

    return this.isPrimary
      ? '/static/icons/SVG/arrow-white-5x10.svg'
      : '/static/icons/SVG/arrow-black-5x10.svg';
  }

  get adobeAnalyticsDataString() {
    return this.ctaData.analyticsName || '';
  }

  get parsedCtaAnalyticsData() {
    if (Object.keys(this.ctaAnalyticsData).length) return this.ctaAnalyticsData;

    this.ctaAnalyticsData = this.getAttribute('cta-analytics-data')
      ? JSON.parse(this.getAttribute('cta-analytics-data'))
      : {};

    this.ctaAnalyticsData.originalObject = this.parsedCtaData;
    return this.ctaAnalyticsData;
  }

  sendAnalyticsEvent(event) {
    if (this.isDownload) {
      handleFileDownloadEvent(analyticsCtaDataObject(this.ctaData));
    } else if (!!this.adobeAnalyticsDataString) {
      const eventData = {
        type: 'click',
        detail: {
          eVar8: this.adobeAnalyticsDataString,
        },
      };
      fireAdobeAnalyticsEvent(eventData);
    } else if (this.parsedCtaAnalyticsData) {
      const data = this.parsedCtaAnalyticsData;
      const ctaAnalyticsObj =
        typeof data === 'string' ? JSON.parse(data) : data;

      ctaAnalyticsObj.originalObject = this.ctaData;

      if (ctaAnalyticsObj.isDownload) {
        handleFileDownloadEvent(ctaAnalyticsObj);
      } else {
        ctaAnalyticsObj.event = event;
        handleAdobeNavEvent(ctaAnalyticsObj);
      }
    }
  }

  get textString() {
    return this.ctaData.text || '';
  }

  get urlString() {
    return this.ctaData.url || undefined;
  }

  get targetValue() {
    return this.ctaData.target || '_self';
  }

  get isDownload() {
    return this.ctaData.isDownload || false;
  }

  get svgClasses() {
    return {
      'smaller-svg': this.isDownload,
    };
  }

  get linkClasses() {
    return {
      'primary-colors': this.isPrimary,
      'secondary-colors': !this.isPrimary,
      hidden: this.hidden,
      disabled: this.disabled,
      'print-none': !this.reserveSpaceWhenHidden,
      'print-hidden': this.reserveSpaceWhenHidden,
    };
  }

  get parsedCtaData() {
    if (this.ctaData) return this.ctaData;

    this.ctaData = this.getAttribute('cta-data')
      ? JSON.parse(this.getAttribute('cta-data'))
      : null;

    return this.ctaData;
  }

  get reserveSpaceWhenHidden() {
    return this.hasAttribute('reserve-space-when-hidden');
  }
}
customElements.define('cta-button', CtaButton);
